import { type ElementType, forwardRef } from "react"
import { twMerge } from "tailwind-merge"
import { Icon } from "../icon"
import type { IconProps } from "../icon/Icon"
import { Tooltip, type TooltipProps } from "../overlays/tooltip/Tooltip"
import { makeIconButtonClassName } from "./IconButton"
import { LinkButton, type LinkButtonProps } from "./LinkButton"

export type IconLinkButtonProps = Omit<LinkButtonProps, "children"> & {
  /** An element that comes from the `@phosphor-icons/react` package. */
  icon: ElementType
  /** Props to pass to the icon such as `weight` */
  iconProps?: Omit<IconProps, "as">
  /** Used as the aria-label and tooltip label */
  label: string
  /** Props to pass to the tooltip */
  tooltipProps?: Omit<TooltipProps, "content">
}

/**
 * A link component styled as an [IconButton](./IconButton.tsx) component. Is
 * wrapped in a `Tooltip` so the user can discover what the button does when
 * hovering.
 *
 * ```tsx
 * import { Star } from "@phosphor-icons/react"
 * import { IconLinkButton } from "@tokenterminal/core/ui/button/IconLinkButton"
 *
 * <IconLinkButton icon={Star} label="Star this" href="..." />
 * ```
 */

export const IconLinkButton = forwardRef<HTMLAnchorElement, IconLinkButtonProps>(
  function IconLinkButton(
    { className, icon, iconProps, label, tooltipProps, size = "medium", ...props },
    ref,
  ) {
    const button = (
      <LinkButton
        ref={ref}
        className={twMerge(makeIconButtonClassName(size), className)}
        aria-label={label}
        {...props}
      >
        <Icon as={icon} className="pointer-events-none" aria-hidden {...iconProps} />
      </LinkButton>
    )

    return (
      <Tooltip content={label} triggerProps={tooltipProps}>
        {button}
      </Tooltip>
    )
  },
)
